// 把目前範圍內每個家戶的 GET /v1/homes/{id}（home + things + members）平行抓回來。
// 範圍 = GET /v1/me/devices 的家戶（我組織的家戶 + 只經分享看到設備的家戶，SPEC #me-devices），再依頂列的範圍切換篩選（lib/scope.tsx）。
// 經組織授權 / 分享的人只拿得到看得到的設備（app-api homeGet 依授權篩 things）；最後再照「我的設備」篩一次（lib/useDevices.ts withAccess）。
// 在線狀態以 /v1/vms/stream 的 presence 幀為準（SPEC W24）：串流連著時只每 5 分鐘對一次帳，斷線時回到 30 秒輪詢；
// 重連成功由 LiveEventsProvider 立刻重撈一次。
import { useQueries } from '@tanstack/react-query';
import { getHomeOf, type HomeDetail, type KinioHome } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import type { ScopeHome } from '../../lib/planeApi';
import { useLiveEvents } from '../../lib/liveEvents';

/** 串流連著 / 斷線時的輪詢間隔。 */
export const HOME_POLL_STREAM_MS = 5 * 60_000;
export const HOME_POLL_MS = 30_000;

export interface HomeResult {
  home: KinioHome;
  detail?: HomeDetail;
  error?: unknown;
  loading: boolean;
  /** 這份資料抓回來的時間（ms）；比它晚的 presence 幀才蓋過去。 */
  updatedAt: number;
}

export function useHomeDetails(): HomeResult[] {
  return useHomeDetailsOf(useScope().visible);
}

/** 指定家戶（深連結：不在目前範圍的家戶也要能看，P0-2）。 */
export function useHomeDetailsOf(homes: ScopeHome[]): HomeResult[] {
  const streaming = useLiveEvents().state === 'open';
  const results = useQueries({
    queries: homes.map((h) => ({
      queryKey: ['home', h.home_id],
      queryFn: () => getHomeOf(h),
      staleTime: 15_000,
      refetchInterval: streaming ? HOME_POLL_STREAM_MS : HOME_POLL_MS,
    })),
  });
  return homes.map((home, i) => ({ home, detail: results[i]?.data, error: results[i]?.error ?? undefined, loading: !!results[i]?.isLoading, updatedAt: results[i]?.dataUpdatedAt ?? 0 }));
}

export function homeLabel(h: KinioHome): string {
  return h.display_name || h.home_id;
}
